// dependencies
import React, { useMemo } from "react";

// styles
import { faBell, faEyeSlash, faShareAlt } from "@fortawesome/pro-solid-svg-icons";

// py-components
import {
	ICellRendererParams,
	Icon,
	Link,
	PYColumnDef,
	SortChangedEvent,
	Table,
	Text,
	Tooltip,
} from "@performyard/py-components";

//hooks
import { SearchServiceSort, useCompanySettings } from "../../../../api/hooks";

import { Goal } from "../../../../api/services/searchServiceResultTypes/goal";

import get from "../../../../utils/GetNested";
import parseListToFormattedString from "../../../../utils/ParseListToFormattedString";
import { appendCurrentLocationAsFrom } from "../../../../utils/goalsBreadcrumbs";

// constants
import GoalConstants from "../../../../constants/Goals";

interface BaseProps {
	goals: Goal[];
	isFetchingGoals: boolean;
	handleColumnHeaderClick: (columnId: SearchServiceSort) => void;
	activeSort: string;
	activeSortDirection: string;
}

// Custom cell renderer for goal name with link
const GoalNameCellRenderer = (params: ICellRendererParams<Goal>) => {
	const goal = params.data;
	if (!goal) return null;

	const goalHref = appendCurrentLocationAsFrom(`/goal/${get(goal, "_id", "")}/`);
	const userCanViewGoal = goal.permissions?.includes("goals:viewDetails") || false;
	const goalName = get(goal, "name", "");

	return (
		<div data-testid="name" className="flex h-full w-full min-w-0 max-w-full items-center justify-start">
			{userCanViewGoal ? (
				<Link href={goalHref}>
					<Text size="paragraph-3" color="primary-5" dangerouslySetClassName="line-clamp-1">
						{goalName}
					</Text>
				</Link>
			) : (
				<div className="w-full min-w-0 py-line-clamp-1">
					<Text size="paragraph-3">{goalName}</Text>
				</div>
			)}
		</div>
	);
};

const TooltipContent = (mainMessage: string, tooltipMessage: string) => {
	return (
		<div>
			<Text
				size="paragraph-3"
				weight="semi-bold"
				dangerouslySetClassName="line-clamp-1 border-b border-neutral-2 pb-2 mb-2 border-solid"
			>
				{mainMessage}
			</Text>
			<Text size="caption" weight="regular">
				{tooltipMessage}
			</Text>
		</div>
	);
};

// Custom cell renderer for goal indicators
const IndicatorsCellRenderer = (params: ICellRendererParams<Goal>) => {
	const goal = params.data;
	if (!goal) return null;

	const isPrivateGoal = get(goal, "transparency", "") === "private";
	const sharers = get(goal, "sharers", []);
	const isSharedGoal = sharers.length > 0;
	const hasUnseenUpdates = get(goal, "hasUpdates", false);
	const shareMessage = isSharedGoal
		? `This goal is shared with ${parseListToFormattedString(sharers.map((sharer) => sharer.name))}`
		: "This goal is not shared.";

	return (
		<div data-testid="indicatorsContainer" className="flex h-full items-center justify-center gap-2">
			<Tooltip
				content={TooltipContent(
					isPrivateGoal ? "Private Goal" : "Public Goal",
					isPrivateGoal ? "This goal is set to private." : "This goal is set to public."
				)}
			>
				<Icon icon={faEyeSlash} color={isPrivateGoal ? "primary-5" : "neutral-3"} />
			</Tooltip>
			<Tooltip content={TooltipContent("Shared Goal", shareMessage)}>
				<Icon icon={faShareAlt} color={isSharedGoal ? "primary-5" : "neutral-3"} />
			</Tooltip>
			<Tooltip
				content={TooltipContent(
					"New update",
					hasUnseenUpdates
						? "There has been a new update since you last viewed this goal."
						: "There are no new updates to this goal."
				)}
			>
				<Icon icon={faBell} color={hasUnseenUpdates ? "primary-5" : "neutral-3"} />
			</Tooltip>
		</div>
	);
};

// Base column definitions (without weight)
const createColumnDefs = (goalWeightEnabled: boolean): PYColumnDef<Goal>[] => {
	const columns: PYColumnDef<Goal>[] = [
		{
			headerName: "Goal",
			field: "name",
			cellRenderer: GoalNameCellRenderer,
			width: goalWeightEnabled ? 400 : 464,
			sortable: true,
			resizable: false,
		},
		{
			headerName: "",
			field: "indicators",
			cellRenderer: IndicatorsCellRenderer,
			width: 90,
			sortable: false,
			resizable: false,
		},
		{
			headerName: "Assignee",
			field: "assignee",
			type: "employee",
			width: 159,
			sortable: true,
			resizable: false,
			valueGetter: (params) => params.data?.assignee,
		},
		{
			headerName: "Due Date",
			field: "endDate",
			type: "date",
			width: 104,
			sortable: true,
			resizable: false,
			cellRendererParams: {
				dateFormat: "MMM d, yyyy",
			},
		},
	];

	// Add weight column if enabled
	if (goalWeightEnabled) {
		columns.push({
			headerName: "Weight",
			field: "goal_weight",
			type: "text",
			width: 80,
			sortable: true,
			resizable: false,
			valueFormatter: (params) => {
				const weight = params.value;
				return weight != null ? String(weight) : "";
			},
		});
	}

	columns.push(
		{
			headerName: "Status",
			field: "status",
			type: "text",
			width: 80,
			sortable: true,
			resizable: false,
			valueFormatter: (params) => {
				return get(GoalConstants, ["statusLabels", params.value], "");
			},
		},
		{
			headerName: "Progress",
			field: "progress",
			type: "progress",
			width: 180,
			sortable: true,
			resizable: false,
			valueGetter: (params) => {
				const goal = params.data;
				if (!goal) return null;
				return get(goal, "progress", 0) * 100;
			},
			cellRendererParams: (goal: Goal) => ({
				colorOverride: get(goal, ["outlook", "color"], undefined),
				ariaLabel: "Goal Progress",
			}),
		}
	);

	return columns;
};

const EmployeeGoalsTable = (props: BaseProps) => {
	const { goals, isFetchingGoals, handleColumnHeaderClick, activeSort, activeSortDirection } = props;
	const { data: companySettings, isLoading: isCompanySettingsLoading } = useCompanySettings();
	const goalWeightEnabled = Boolean(companySettings?.functions?.goals?.attributes["goalWeights"]);

	// Memoize column definitions to prevent recreation on every render
	const columnDefs = useMemo(() => createColumnDefs(goalWeightEnabled), [goalWeightEnabled]);

	// Handle sort changes from AG Grid
	const onSortChanged = (event: SortChangedEvent<Goal>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn && sortedColumn.colId) {
			handleColumnHeaderClick({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	return (
		<div data-testid="goalListContainer">
			<Table<Goal>
				autoHeight
				columnSizing="fit-grid"
				rowData={goals}
				columnDefs={columnDefs}
				loading={isFetchingGoals || isCompanySettingsLoading}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText={"No Goals Found"}
				// Set initial sort state from props
				initialState={{
					sort: {
						sortModel: activeSort
							? [
									{
										colId: activeSort,
										sort: activeSortDirection as "asc" | "desc",
									},
								]
							: [],
					},
				}}
				animateRows={false}
			/>
		</div>
	);
};

export default EmployeeGoalsTable;
